import { components, paths } from "@/api-schema";
import {
  PathsV1OccupationSearchPostParametersQueryOrderByDirection,
  PathsV1OccupationSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useOccupationDeleteMutation } from "@/common/api/occupation/use-occupation-delete.mutation";
import { useOccupationRestoreMutation } from "@/common/api/occupation/use-occupation-restore.mutation";
import { useOccupationSearchQuery } from "@/common/api/occupation/use-occupation-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures, TimestampSortField } from "@/components/data-table/types";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { occupationTableDefaultColumns } from "./occupation-table-columns";

type Occupation = components["schemas"]["Occupation"] & RowWithIdentifier;
type OccupationSorting = NonNullable<paths["/v1/occupation/search"]["post"]["parameters"]["query"]["orderBy"]>;
type OccupationQueryParams = paths["/v1/occupation/search"]["post"]["parameters"]["query"];
type OccupationRequestBody = paths["/v1/occupation/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [occupationTableDefaultColumns.columnName, occupationTableDefaultColumns.columnDeletedAt];

const OCCUPATION_TABLE_CONFIG: PredefinedTableConfig<OccupationSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1OccupationSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1OccupationSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchOccupations"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: true,
  } as const,
  sortingMapping: {
    name: ["name"],
    deletedAt: ["deletedAt"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
  sortColumns: [
    { id: PathsV1OccupationSearchPostParametersQueryOrderByField.Name, label: () => i18next.t("common:Name") },
  ],
  timestampSortFields: [TimestampSortField.CreatedAt, TimestampSortField.UpdatedAt, TimestampSortField.DeletedAt],
} as const;

const OCCUPATION_TABLE_QUERY_HOOKS: DataFetchHooks<Occupation, OccupationQueryParams, OccupationRequestBody> = {
  useSearchQuery: useOccupationSearchQuery,
  useDeleteMutation: useOccupationDeleteMutation,
  useRestoreMutation: useOccupationRestoreMutation,
} as const;

export interface OccupationTableProps extends BasePredefinedTableProps<
  Occupation,
  OccupationSorting,
  OccupationQueryParams,
  OccupationRequestBody
> {}

const BaseOccupationTable = createBasePredefinedTable({
  config: OCCUPATION_TABLE_CONFIG,
  queryHooks: OCCUPATION_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function OccupationTable(props: OccupationTableProps) {
  const { t } = useTranslation(["common"]);
  const {
    name = "occupation-table",
    showCreateButton = true,
    body = { deletedAt: [DeletedStatus.ACTIVE, DeletedStatus.DELETED] },
    filters,
    filterValues,
    columns,
    ...restProps
  } = props;

  const defaultFilters = useMemo(() => filters ?? [new DeletedStatusFilter()], [filters]);
  const defaultFilterValues = useMemo(() => filterValues ?? { deletedAt: [DeletedStatus.ACTIVE] }, [filterValues]);

  return (
    <BaseOccupationTable
      searchPlaceholder={t("common:SearchOccupations")}
      {...{
        name,
        showCreateButton,
        body,
        columns,
        filters: defaultFilters,
        filterValues: defaultFilterValues,
        confirmActions: ["delete", "restore"],
        toastMessages: {
          deleteSuccess: t("OccupationHasBeenDeleted"),
          deleteError: t("DeleteOccupationError"),
          restoreSuccess: t("OccupationHasBeenRestored"),
          restoreError: t("RestoreOccupationError"),
        },
        confirmLabels: {
          delete: (row: Occupation) => ({
            title: row.deletedAt ? t("DeleteOccupationPermanently") : t("DeleteOccupation"),
            text: row.deletedAt ? t("DeleteOccupationPermanentlyDescription") : t("DeleteOccupationDescription"),
            confirmButton: row.deletedAt ? t("YesDeletePermanently") : t("ram:YesDelete"),
            cancelButton: t("Cancel"),
          }),
          restore: {
            title: t("RestoreOccupation"),
            text: t("RestoreOccupationDescription"),
            confirmButton: t("YesRestore"),
            cancelButton: t("Cancel"),
          },
        },
        ...restProps,
      }}
    />
  );
}
